import React from 'react';
import { Alert, AlertTitle, Box, IconButton, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { BaseBannerProps } from '../types';
import { BANNER_DIV_ID } from 'constants/HTMLAttributes';

interface BaseBannerInternalProps extends BaseBannerProps {
  /** Whether to show close button */
  showCloseButton?: boolean;
  
  /** Custom close button element */
  closeButton?: React.ReactNode;
  
  /** Custom action button element */
  actionButton?: React.ReactNode;
  
  /** Additional content to render */
  children?: React.ReactNode;
}

const BaseBanner: React.FC<BaseBannerInternalProps> = ({
  message,
  title,
  severity = 'info',
  showIcon = true,
  icon,
  className,
  sx,
  onDismiss,
  showCloseButton = false,
  closeButton,
  actionButton,
  children,
  testId,
}) => {
  const defaultSx = {
    position: 'relative',
    paddingRight: showCloseButton ? '48px' : undefined,
    padding: '28px 24px',
    backgroundColor: '#F3F4F4',
    color: '#000000',
    borderRadius: 2,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    border: 'none',
    '& .MuiAlert-icon': {
      fontSize: '18px',
      marginRight: '16px',
      alignSelf: 'baseline',
      display: 'flex',
      alignItems: 'baseline',
      color: '#000000',
      '& .MuiSvgIcon-root': {
        fontWeight: 'normal',
        color: '#000000',
        fontSize: '18px',
        verticalAlign: 'baseline',
      },
    },
    '& .MuiAlert-message': {
      width: '100%',
      padding: 0,
    },
    fontFamily: 'Mullish, Arial, sans-serif',
    ...sx,
  };

  return (
    <Alert
      id={BANNER_DIV_ID}
      severity={severity}
      icon={showIcon ? icon : false}
      className={className}
      sx={defaultSx}
      data-testid={testId}
      variant="outlined"
    >
      {showCloseButton && (
        <IconButton
          onClick={onDismiss}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            padding: '4px',
            color: '#000000',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
          size="small"
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      
      {title && (
        <AlertTitle 
          sx={{ 
            fontWeight: 'bold',
            color: '#000000',
            fontFamily: 'Mullish, Arial, sans-serif',
            marginBottom: 0.5,
          }}
        >
          {title}
        </AlertTitle>
      )}
      
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 1 },
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              fontFamily: 'Mullish, Arial, sans-serif',
              color: '#000000',
              '& strong, & b': {
                fontWeight: 'bold',
              },
              '& *:not(strong):not(b)': {
                fontWeight: 'normal',
              },
            }}
            dangerouslySetInnerHTML={{ __html: message }}
          />
          {children}
        </Box>
        
        {(actionButton || closeButton) && (
          <Box 
            sx={{ 
              display: 'flex',
              gap: 1,
              alignSelf: { xs: 'flex-start', sm: 'center' },
            }}
          >
            {actionButton}
            {closeButton}
          </Box>
        )}
      </Box>
    </Alert>
  );
};

export default BaseBanner; 